import React, { useState, useEffect } from "react";
import Table from "../../../../../Components/UiComponents/Table/Table";
import { useNavigate } from "react-router-dom";
import { EditConvgIcon } from "../../../../../Components/IconComponents";
import { useDispatch } from "react-redux";
import { SopInstancedata } from "../../../../../JsonData/CreateTaskConfig";
import { setTableData } from "../../../../../CentralStore/ExportLMSTable/dataSlice";
import { getExportedColumnsData } from "../../../../../Components/UiComponents/utils";
import {
  sopInstanceList,
  getThemeURL,
} from "../../../../../Services/EndPoints";
import { getData } from "../../../../../Services/Services";
import Moment from "react-moment";
import { toast } from "react-toastify";
import Loader from "react-spinner-loader";
import { useTranslation } from "react-i18next";

const SopInstanceTable = () => {
  const [data, setData] = useState([]); // Table data
  const [loader, setLoader] = useState(false); // Loader state
  const [rowCount, setRowCount] = useState(0); // Total row count
  const [searchData, setSearchData] = useState([]); // Data from search results

  // User permissions state
  const [userPermissions, setUserPermissions] = useState({
    canView: false,
    canEdit: false,
    canDelete: false,
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Parse web permissions from user profile
  const parseUserPermissions = (webPermissions) => {
    if (!webPermissions) {
      return { canView: false, canEdit: false, canDelete: false };
    }
    const permissions = webPermissions
      .split(",")
      .map((p) => p.trim().toUpperCase());

    return {
      canView: permissions.includes("V"),
      canEdit: permissions.includes("E"),
      canDelete: permissions.includes("D"),
    };
  };

  // Fetch user profile to determine permissions
  const getUserProfile = async () => {
    try {
      const userProfile = await getData(getThemeURL);
      if (userProfile && userProfile.length > 0) {
        // Parse and set user permissions
        const permissions = parseUserPermissions(
          userProfile[0].web_permissions
        );
        setUserPermissions(permissions);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  // Fetch table data on component mount
  useEffect(() => {
    getUserProfile(); // Fetch user permissions on component mount
    getTaskLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Send filtered data to central store whenever search data changes
  useEffect(() => {
    sendDataToCentralStore(searchData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData]);

  /**
   * Truncate text and add tooltip with full text
   */
  const getShortenedText = (strLength, fulltext) => {
    if (!fulltext) return "NA";
    return (
      <span title={fulltext}>
        {fulltext.length > strLength
          ? fulltext.substring(0, strLength) + "..."
          : fulltext}
      </span>
    );
  };

  /**
   * API call to get SOP instance list
   */
  async function getTaskLists() {
    setLoader(true);
    const taskList = await getData(sopInstanceList);
    setLoader(false);
    setData(taskList);
    setRowCount(taskList.length);
  }

  /**
   * Store filtered table data for export
   */
  const sendDataToCentralStore = (data) => {
    if (data.length > 0) {
      const columns = SopInstancedata;
      dispatch(
        setTableData({
          columns,
          tableData: getExportedColumnsData(columns, data),
        })
      );
    }
  };

  /**
   * Table column definitions
   */
  const columns = React.useMemo(
    () => [
      {
        Header: t("sopinstancelist.id"),
        accessor: "id",
        disableSortBy: true,
        Cell: (props) => getShortenedText(15, props.value),
      },
      {
        Header: t("sopinstancelist.name"),
        accessor: "name",
        disableSortBy: true,
        Cell: (props) => props.value || "NA",
      },
      {
        Header: t("sopinstancelist.sop"),
        accessor: "sop",
        disableSortBy: true,
      },
      {
        Header: t("sopinstancelist.incident"),
        accessor: "incident",
        disableSortBy: true,
        Cell: (props) => props.value || "NA",
      },
      {
        Header: t("sopinstancelist.status"),
        accessor: "status",
        disableSortBy: false,
        Cell: ({ value }) => {
          if (!value) return t("NA");

          // Status color mapping
          const statusMap = {
            Started: { color: "#519EFE", label: t("Started") },
            "In-Progress": { color: "#fccd7c", label: t("In Progress") },
            Completed: { color: "#7cdb90", label: t("Completed") },
            Cancel: { color: "#e95060", label: t("Canceled") },
            Closed: { color: "Gray", label: t("Closed") },
          };

          const status = statusMap[value];

          return status ? (
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  backgroundColor: status.color,
                  borderRadius: "50%",
                }}
              ></span>
              {status.label}
            </div>
          ) : (
            t("NA")
          );
        },
      },
      {
        Header: t("sopinstancelist.createdby"),
        accessor: "created_by",
        disableSortBy: true,
      },
      {
        Header: t("sopinstancelist.createdon"),
        accessor: "created_on",
        disableSortBy: false,
        Cell: (props) =>
          props.value ? (
            <Moment format="DD-MM-YYYY, hh:mm A">{props.value}</Moment>
          ) : (
            "NA"
          ),
      },
      // Only show actions column if user has edit permission
      ...(userPermissions.canEdit
        ? [
            {
              Header: t("sopinstancelist.action"),
              disableSortBy: true,
              Cell: (props) => {
                return (
                  <div className="usertable__actionsicon">
                    {/* Edit Button - only show if user has edit permission */}
                    {userPermissions.canEdit && (
                      <span
                        onClick={() =>
                          navigate("/sop-management/sops-instance", {
                            state: { data: props.row.original },
                          })
                        }
                      >
                        <EditConvgIcon className="cursor-pointer me-2" />
                      </span>
                    )}
                    {/* Future delete feature can be enabled here */}
                  </div>
                );
              },
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, userPermissions] // Add userPermissions as dependency
  );

  return (
    <div>
      <div className="create-user">
        {/* Loader */}
        <Loader
          show={loader}
          type="body"
          stack="vertical"
          message="Loading Data"
        />

        {/* Data Table */}
        <div className="datatable__container">
          <Table
            data={data}
            title=""
            showFilter={false}
            setSearchData={setSearchData}
            columns={columns}
            numberOfRows={10}
            showExport={false}
            showReset={false}
            onExpand={false}
            showSearch={true}
            isHeaderFixed={true}
            rowCount={rowCount}
            headerTableHeadStyle={{ background: "#1a7a73", color: "#FFFFFF" }}
            tableStyle={{
              border: "1px solid #f0f0f0",
              padding: "0px",
              borderRadius: "12px",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default SopInstanceTable;
